import React from 'react';
import { useTranslation } from 'react-i18next';
import './MessagePopup.css';

/**
 * MessagePopup Component
 * 
 * A reusable popup/modal component for displaying messages to the user.
 * Can be used for errors, warnings, info messages, etc.
 * 
 * @param {boolean} isOpen - Whether the popup is visible
 * @param {function} onClose - Callback when popup is dismissed
 * @param {string} title - Popup title (can be a translation key or plain text)
 * @param {string} message - Popup message content
 * @param {string} type - Popup type: 'error', 'warning', 'info', 'success' (default: 'error')
 * @param {string} dismissText - Text for dismiss button (optional, uses translation if not provided)
 * @param {function} onDismiss - Optional callback when dismiss button is clicked (in addition to onClose)
 */
export default function MessagePopup({
  isOpen,
  onClose,
  title,
  message,
  type = 'error',
  dismissText,
  onDismiss
}) {
  const { t } = useTranslation();

  if (!isOpen) {
    return null;
  }

  const handleDismiss = () => {
    if (onDismiss) {
      onDismiss();
    }
    onClose();
  };

  const handleBackdropClick = (e) => {
    // Only close if clicking the backdrop, not the popup content
    if (e.target === e.currentTarget) {
      handleDismiss();
    }
  };

  // Determine CSS classes based on type
  const overlayClass = `message-popup-overlay ${type}-overlay`;
  const messageClass = `message-popup-content ${type}-message`;

  // Default dismiss text based on type
  const defaultDismissText = type === 'error' 
    ? t('game.wrongWord.dismiss', 'Try Again')
    : t('common.ok', 'OK');

  return (
    <div 
      className={overlayClass}
      onClick={handleBackdropClick}
    >
      <div className={messageClass}>
        <h2>{title}</h2>
        <div className="message-popup-body">
          <p className="message-popup-text">{message}</p>
        </div>
        <div className="message-popup-actions">
          <button
            className={`message-dismiss-btn ${type}-dismiss-btn`}
            onClick={handleDismiss}
          >
            {dismissText || defaultDismissText}
          </button>
        </div>
      </div>
    </div>
  );
}

